/**
 * Use this file for helper funcs for Search Page components
 */

import {
    COVERAGE_TYPE_CODE,
    CARE_PATH_CATEGORY_CODE,
    CATEGORY_CODE_MAP,
    PROVIDER_TYPE_CONVERSION_FPC,
    PH_CALIFORNIA_POLICIES,
    VISION_MATERIALS,
    NO_BENEFITS,
    LOW_USAGE_CODE,
    REV_DUPLICATE,
} from '../constants/index';

const revCodesServiceTypes = [
    'RevPercentBilledCharges',
    'RevUnableToValidate',
    'RevCptNeeded',
    'Unlisted',
    'RevNoEstimate',
];

/**
 * retrieves networks and coverage effective dates from the benefits response
 * matches coverage in eligibility based on on the care path category of the selected services
 * also passes along indemnityFlag status if present
 * @param {Object} memberDetails - member details from eligibility response
 * @param {string} carePathCategoryType - from carePath details for selected service
 */
export const getCoverageFromMemberDetails = (memberDetails, carePathCategoryType) => {
    let relevantCoverage = null;

    memberDetails.eligibility.forEach((eligibility) => {
        eligibility.memberHealthCoverage.coverageType.forEach((coverage) => {
            const coverageTypeCode = coverage?.typeCode?.coverageTypeCode;
            const carePathCategoryCode = carePathCategoryType.substring(0, 1).toUpperCase();
            if (
                coverageTypeCode === carePathCategoryCode ||
                (coverageTypeCode == COVERAGE_TYPE_CODE.OPH &&
                    carePathCategoryCode == CARE_PATH_CATEGORY_CODE.MEDICAL)
            ) {
                relevantCoverage = {
                    coverageEffDateRange: coverage.coverageEffDateRange,
                    networks: coverage.networks,
                };
            }
            if (relevantCoverage && coverage?.indemnityFlag) {
                relevantCoverage.indemnityFlag = coverage.indemnityFlag;
            }
        });
    });
    return relevantCoverage;
};

/**
 * Finds the carepathStepId and carepathCategoryCode of the selected shoppable service
 * @param {Object} selectedServiceValue
 * @returns {Array[String]}
 */
export const getCarePathIdAndCategoryCode = (selectedServiceValue) => {
    return [
        selectedServiceValue?.carePathStep?.id,
        CATEGORY_CODE_MAP[selectedServiceValue?.fullCarePath?.coverageTypeCode],
        PROVIDER_TYPE_CONVERSION_FPC[selectedServiceValue?.carePathStep?.providerType],
    ];
};

export const checkIndemnityCoverages = (memberDetails, checkForUMR = false) => {
    const isIndemnity = {};
    memberDetails.eligibility.forEach((eligibility) => {
        eligibility.memberHealthCoverage.coverageType.forEach((coverage) => {
            if (memberDetails?.sourceCode != 'UM' || checkForUMR) {
                //UMR indemnity population is a special case, so exclude here unless checking for it explicitly
                isIndemnity[coverage?.typeCode?.coverageTypeCode] = coverage.indemnityFlag;
            }
        });
    });
    return isIndemnity;
};

export const checkForRevNoEstimateServiceType = (serviceTypes) => {
    return serviceTypes?.includes('RevNoEstimate');
};

export const checkForVisionMaterialsServiceType = (serviceTypes) => {
    return serviceTypes?.includes(VISION_MATERIALS);
};
export const checkIfMemberHasVisionCoverageType = (memberDetails) => {
    return memberDetails?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.some(
        (coverageType) => coverageType?.typeCode?.coverageTypeCode === 'V'
    );
};
export const checkForNoBenefitsVisionServiceType = (serviceTypes) => {
    return serviceTypes?.includes(VISION_MATERIALS) && serviceTypes?.includes(NO_BENEFITS);
};
export const checkForRevcodeOrVisionMaterialsServiceType = (serviceTypes) => {
    const revCodeOrVisionMaterialsServiceTypes = revCodesServiceTypes.toSpliced(
        0,
        0,
        VISION_MATERIALS,
        LOW_USAGE_CODE
    );
    return serviceTypes?.some((serviceType) =>
        revCodeOrVisionMaterialsServiceTypes.includes(serviceType)
    );
};
export const checkForLowUsageCodeServiceType = (serviceTypes) => {
    return serviceTypes?.includes(LOW_USAGE_CODE) && serviceTypes?.includes(REV_DUPLICATE);
};
export const checkBehavioralMedicalDisclaimerCondition = (fullCarePath) => {
    return (
        fullCarePath?.serviceTypes?.includes('BehavioralMedical') &&
        fullCarePath?.ineligibleForCosts === true
    );
};

export const shouldNotPerformProviderSearch = (
    serviceTypes,
    coverageTypeCode,
    memberDetails,
    selectedServiceValue
) => {
    return (
        checkForLowUsageCodeServiceType(serviceTypes) ||
        checkForRevcodeOrVisionMaterialsServiceType(serviceTypes) ||
        shouldShowDentalPolicyDisclaimer(coverageTypeCode, memberDetails)
            ?.showDentalPolicyDisclaimer ||
        checkForPHCalifornia(selectedServiceValue.fullCarePath, memberDetails) ||
        checkBehavioralMedicalDisclaimerCondition(selectedServiceValue.fullCarePath)
    );
};

export const checkForRevcodeServiceType = (serviceTypes) => {
    return serviceTypes?.some((serviceType) => revCodesServiceTypes.includes(serviceType));
};

export const shouldShowDentalPolicyDisclaimer = (coverageTypeCode, memberDetails) => {
    const udSourceCode = checkForUDSourceCode(coverageTypeCode, memberDetails);
    const allsaversOrGriDental = checkForAllsaversOrGriDental(coverageTypeCode, memberDetails);
    const studentResourcesDental = checkForStudentResourcesDental(coverageTypeCode, memberDetails);
    return {
        showDentalPolicyDisclaimer: udSourceCode || allsaversOrGriDental || studentResourcesDental,
        udSourceCode: udSourceCode,
        allsaversOrGriDental: allsaversOrGriDental,
        studentResourcesDental: studentResourcesDental,
    };
};

export const checkForUDSourceCode = (coverageTypeCode, memberDetails) => {
    return memberDetails?.sourceCode == 'UD' && coverageTypeCode != 'D';
};

export const checkForAllsaversOrGriDental = (coverageTypeCode, memberDetails) => {
    return ['AL', 'GR'].includes(memberDetails?.sourceCode) && coverageTypeCode == 'D';
};

export const checkForStudentResourcesDental = (coverageTypeCode, memberDetails) => {
    if (memberDetails?.sourceCode == 'SR' && coverageTypeCode == 'D') {
        return memberDetails?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.some(
            (coverageType) => coverageType?.typeCode?.coverageTypeCode === 'D'
        );
    }
    return false;
};

/**
 * Checks if the member is a PH California member and the selected service is not a PH specialty
 * @param {Object} serviceObj - Full carepath response
 * @param {Object} memberDetails - member details from eligibility response
 * @returns boolean - true if it's a PH California member and the selected service is not a PH specialty,false otherwise
 */
export const checkForPHCalifornia = (serviceObj, memberDetails) => {
    if (
        memberDetails?.sourceCode == 'PH' &&
        PH_CALIFORNIA_POLICIES.includes(memberDetails?.policyNumber.toUpperCase())
    ) {
        // A non-PH code is any code that doesn't have the specialty id 0(Acupuncture) or 44(Chiropractic Medicine) in its primary or restricted specialties
        const phSpecialties = [0, 44];
        let hasPhSpecialty = false;
        serviceObj?.carePathSteps.forEach((carePathStep) => {
            if (
                phSpecialties.some(
                    (targetId) =>
                        carePathStep?.primarySpecialties.some(
                            (specialty) => specialty.internalSpecialtyId === targetId
                        ) ||
                        carePathStep?.restrictedSpecialties.some(
                            (specialty) => specialty.internalSpecialtyId === targetId
                        )
                )
            ) {
                hasPhSpecialty = true;
            }
        });
        // negating hasSpecialty to return false if PH specialties present to continue normal flow.
        return !hasPhSpecialty;
    }
    return false;
};
